import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './GetStartedModal.module.css';
import { ProfileIcon, TeamIcon, ShopIcon } from './MarketingIcons';
import { trackEvent } from '../../utils/analytics';

const SHOP_URL = 'https://veloxco.com';

/**
 * Destination for every "Get started" call to action.
 *
 * Three ways in, in this order: a digital profile with no card required,
 * a team conversation, or a physical card (standard from the shop, or a
 * custom design through the existing enquiry).
 */
const GetStartedModal = ({ onClose, onChooseEnquiry }) => {
  const dialogRef = useRef(null);
  const firstOptionRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    firstOptionRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const choose = (audience) => {
    trackEvent('Marketing CTA', 'get_started_choice', audience);
    onChooseEnquiry(audience);
  };

  return (
    <div
      className={styles.overlay}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="get-started-title"
        ref={dialogRef}
      >
        <div className={styles.head}>
          <div>
            <p className="vxm-eyebrow">Get started</p>
            <h2 id="get-started-title" className={styles.title}>
              How do you want to start?
            </h2>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <ul className={styles.options}>
          <li>
            <Link
              to="/register"
              className={styles.option}
              ref={firstOptionRef}
              onClick={() => {
                trackEvent('Marketing CTA', 'create_profile_click', 'get_started_modal');
                onClose();
              }}
            >
              <span className={styles.optionIcon}>
                <ProfileIcon />
              </span>
              <span className={styles.optionBody}>
                <span className={styles.optionTitle}>Create your profile</span>
                <span className={styles.optionText}>
                  Start using Velox with a digital profile. Capture contacts,
                  keep the context, and add a card later if you want one.
                </span>
                <span className={styles.optionMeta}>Create account &rarr;</span>
              </span>
            </Link>
          </li>

          <li>
            <button
              type="button"
              className={styles.option}
              onClick={() => choose('team')}
            >
              <span className={styles.optionIcon}>
                <TeamIcon />
              </span>
              <span className={styles.optionBody}>
                <span className={styles.optionTitle}>Velox for a team</span>
                <span className={styles.optionText}>
                  A shared contact hub, seats for your people, and cards if you
                  want them. Tell us how you work and we will take it from there.
                </span>
                <span className={styles.optionMeta}>Tell us about your team &rarr;</span>
              </span>
            </button>
          </li>

          <li>
            <div className={`${styles.option} ${styles.optionStatic}`}>
              <span className={styles.optionIcon}>
                <ShopIcon />
              </span>
              <span className={styles.optionBody}>
                <span className={styles.optionTitle}>Get a card</span>
                <span className={styles.optionText}>
                  A contactless card is one way to share in the room. Order a
                  standard design today, or ask us to design one with you.
                </span>
                <span className={styles.optionActions}>
                  <a
                    href={SHOP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.optionAction}
                    onClick={() => trackEvent('Marketing CTA', 'shop_click', 'get_started_modal')}
                  >
                    Order a standard card
                  </a>
                  <button
                    type="button"
                    className={styles.optionAction}
                    onClick={() => choose('professional')}
                  >
                    Request a custom card
                  </button>
                </span>
              </span>
            </div>
          </li>
        </ul>

        <p className={styles.foot}>
          Already have a Velox account?{' '}
          <Link to="/login" className="vxm-link" onClick={onClose}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default GetStartedModal;
